import dayjs from 'dayjs';

import { type DateRangePresetLabel, getPresetRange } from '@prerender/design-system/DateRangePicker';

import { DASHBOARD_DEFAULT_PRESET, getDefaultDateRange } from '~components/Filters/defaultDateRange';
import type { CrawlerFilter } from '~features/crawler-filter';

export type DateWindow = { dateFrom: Date; dateTo: Date };
export type ScopeWindow = DateWindow & { label: DateRangePresetLabel };

export const WIDER_PRESET_LABEL: DateRangePresetLabel = 'last 30 days';

function presetWindow(label: DateRangePresetLabel): ScopeWindow {
  const { from, end } = getPresetRange(label);
  return { dateFrom: from.toDate(), dateTo: end.toDate(), label };
}

export function widenedDateWindow(): DateWindow {
  const { dateFrom, dateTo } = presetWindow(WIDER_PRESET_LABEL);
  return { dateFrom, dateTo };
}

export type FilterSelection = { allDomains: boolean; crawlerFilter: CrawlerFilter; dateFrom: Date; dateTo: Date };

export function scopeCoversDefault({ allDomains, crawlerFilter, dateFrom, dateTo }: FilterSelection): boolean {
  return allDomains && crawlerFilter.kind === 'all' && dateRangeContains({ dateFrom, dateTo }, getDefaultDateRange());
}

export function offeredScopeWindow(coversDefault: boolean): ScopeWindow {
  return coversDefault
    ? presetWindow(WIDER_PRESET_LABEL)
    : { ...getDefaultDateRange(), label: DASHBOARD_DEFAULT_PRESET };
}

export function dateRangeContains(range: DateWindow, window: DateWindow): boolean {
  return !dayjs(range.dateFrom).isAfter(window.dateFrom, 'day') && !dayjs(range.dateTo).isBefore(window.dateTo, 'day');
}

export type ScopeCount = { value: number | undefined; isFetching: boolean };

export type ScopePopulation = { of: number; window: DateRangePresetLabel };

export type EmptyScopeState =
  | { kind: 'no-data' }
  | { kind: 'stalled'; windowLabel: DateRangePresetLabel }
  | { kind: 'default-window-empty'; population?: ScopePopulation }
  | { kind: 'filtered'; hiddenFilters?: string[]; population?: ScopePopulation };

export type EmptyScopeKind = EmptyScopeState['kind'];

export type ScopeCountFacts = {
  emptyInRange: boolean;
  accountEverCrawled: boolean;
  coversDefault: boolean;
  rangeContainsOfferedWindow: boolean;
};

export type EmptyScopeInput = ScopeCountFacts & {
  scopeWindow: ScopeWindow;
  count: ScopeCount;
  selectedCrawlerLabels: string[];
};

export type EmptyScope = {
  counting: boolean;
  state: EmptyScopeState;
};

function emptyDefaultScopeHoldsOfferedWindow({
  emptyInRange,
  coversDefault,
  rangeContainsOfferedWindow,
}: ScopeCountFacts): boolean {
  return emptyInRange && coversDefault && rangeContainsOfferedWindow;
}

export function scopeNeedsCount(facts: ScopeCountFacts): boolean {
  return facts.emptyInRange && facts.accountEverCrawled && !emptyDefaultScopeHoldsOfferedWindow(facts);
}

function emptyScopeState(input: EmptyScopeInput): EmptyScopeState {
  const { emptyInRange, accountEverCrawled, coversDefault, scopeWindow, count, selectedCrawlerLabels } = input;
  if (!accountEverCrawled) return { kind: 'no-data' };

  const population = count.value ? { of: count.value, window: scopeWindow.label } : undefined;
  if (!coversDefault) {
    return {
      kind: 'filtered',
      // The pickers collapse to a count past one selection, so only then do the names need saying.
      hiddenFilters: selectedCrawlerLabels.length > 1 ? selectedCrawlerLabels : undefined,
      population,
    };
  }

  const widerWindowEmpty =
    emptyDefaultScopeHoldsOfferedWindow(input) || (emptyInRange && !count.isFetching && count.value === 0);
  if (widerWindowEmpty) return { kind: 'stalled', windowLabel: scopeWindow.label };

  return { kind: 'default-window-empty', population };
}

export function resolveEmptyScope(input: EmptyScopeInput): EmptyScope {
  return {
    counting: scopeNeedsCount(input) && input.coversDefault && input.count.isFetching,
    state: emptyScopeState(input),
  };
}
